import { useState, useEffect, useCallback } from 'react';
import { api, formatCurrency, getCurrentMonth } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Activity, ChevronLeft, ChevronRight, Check, X, ArrowRight, Landmark, Upload, FileSpreadsheet, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const Rapprochement = () => {
  const [currentMonth, setCurrentMonth] = useState(getCurrentMonth());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);

  const MOIS_FR = ['Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'];

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get(`/rapprochement/${currentMonth}`);
      setData(res.data);
    } catch { toast.error('Erreur chargement'); }
    setLoading(false);
  }, [currentMonth]);

  useEffect(() => { setLoading(true); fetchData(); }, [fetchData]);

  const handleImport = async () => {
    setImporting(true);
    try {
      const res = await api.post('/import/daily-sales', { mois: currentMonth });
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur import');
    }
    setImporting(false);
  };

  const navigateMonth = (dir) => {
    const [y, m] = currentMonth.split('-').map(Number);
    let newM = m + dir, newY = y;
    if (newM > 12) { newM = 1; newY++; }
    if (newM < 1) { newM = 12; newY--; }
    setCurrentMonth(`${newY}-${String(newM).padStart(2, '0')}`);
  };

  const moisLabel = MOIS_FR[parseInt(currentMonth.split('-')[1]) - 1] || '';

  if (loading) return (
    <div className="flex flex-col items-center justify-center py-32 gap-4">
      <Activity size={28} className="animate-pulse-slow" style={{ color: 'rgb(var(--lm-gold))' }} />
      <span className="text-xs tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))]">Chargement</span>
    </div>
  );

  const r = data?.resume || {};
  const hasData = r.ca_ht > 0;

  return (
    <div className="space-y-5" data-testid="rapprochement-page">
      {/* Header */}
      <div className="flex items-center justify-between animate-slide-up">
        <div>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[rgb(var(--lm-text-muted))] mb-1">Rapprochement bancaire</p>
          <h1 className="font-title text-2xl text-[rgb(var(--lm-text))] flex items-center gap-2">
            <Landmark size={20} style={{ color: 'rgb(var(--lm-gold))' }} />
            Rapprochement
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => navigateMonth(-1)} className="p-1.5 rounded-sm hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] transition-colors" data-testid="prev-month">
            <ChevronLeft size={16} />
          </button>
          <span className="font-title text-sm text-[rgb(var(--lm-text))] min-w-[120px] text-center">
            {moisLabel} {currentMonth.slice(0, 4)}
          </span>
          <button onClick={() => navigateMonth(1)} className="p-1.5 rounded-sm hover:bg-[rgb(var(--lm-surface-elevated))] text-[rgb(var(--lm-text-muted))] transition-colors" data-testid="next-month">
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Import button */}
      {!hasData && (
        <div className="lm-card p-6 text-center animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards' }}>
          <FileSpreadsheet size={32} className="mx-auto mb-3 text-[rgb(var(--lm-text-muted))]" />
          <p className="text-sm text-[rgb(var(--lm-text-secondary))] mb-3">Pas de donnees pour {moisLabel} {currentMonth.slice(0, 4)}</p>
          {currentMonth === '2025-01' && (
            <Button onClick={handleImport} disabled={importing} className="bg-[rgb(var(--lm-gold))] text-[rgb(var(--lm-accent-text))]" data-testid="import-btn">
              <Upload size={14} className="mr-2" />
              {importing ? 'Import...' : 'Importer Janvier 2025'}
            </Button>
          )}
        </div>
      )}

      {/* Resume */}
      {hasData && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 animate-slide-up" style={{ animationDelay: '60ms', animationFillMode: 'backwards' }}>
            <KPI label="CA HT" value={formatCurrency(r.ca_ht)} color="gold" />
            <KPI label="Total CB" value={formatCurrency(r.total_cb)} color="text" />
            <KPI label="Total Especes" value={formatCurrency(r.total_especes)} color="text" />
            <KPI label="Versements banque" value={formatCurrency(r.total_versements_banque)} color="green" />
          </div>

          <div className="grid grid-cols-3 gap-3 animate-slide-up" style={{ animationDelay: '90ms', animationFillMode: 'backwards' }}>
            <KPI label="Cheques" value={formatCurrency(r.total_cheques)} color="text" />
            <KPI label="Sorties caisse" value={formatCurrency(r.total_sorties_caisse)} color="red" />
            <KPI label="Rapproches" value={`${data?.nb_rapproches || 0} / ${(data?.nb_rapproches || 0) + (data?.nb_non_rapproches || 0)}`} color={data?.nb_non_rapproches === 0 ? 'green' : 'gold'} />
          </div>

          {/* Versements banque */}
          <div className="animate-slide-up" style={{ animationDelay: '120ms', animationFillMode: 'backwards' }}>
            <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Versements banque (caisse)</p>
            <div className="space-y-1">
              {(data?.versements || []).map((v, i) => (
                <div key={i} className="lm-card px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-[10px] font-mono text-[rgb(var(--lm-text-muted))]">{v.date}</span>
                    <span className="font-mono text-sm" style={{ color: 'rgb(var(--lm-green))' }}>{formatCurrency(v.montant)}</span>
                  </div>
                  {data?.rapprochements?.find(r => r.date_caisse === v.date) ? (
                    <span className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-sm" style={{ background: 'rgb(var(--lm-green) / 0.08)', color: 'rgb(var(--lm-green))' }}>
                      <Check size={10} /> Rapproche
                    </span>
                  ) : (
                    <span className="text-[10px] flex items-center gap-1 px-2 py-0.5 rounded-sm" style={{ background: 'rgb(var(--lm-gold) / 0.08)', color: 'rgb(var(--lm-gold))' }}>
                      <AlertTriangle size={10} /> En attente
                    </span>
                  )}
                </div>
              ))}
              {(data?.versements || []).length === 0 && (
                <div className="lm-card p-4 text-center text-xs text-[rgb(var(--lm-text-muted))]">Aucun versement banque ce mois</div>
              )}
            </div>
          </div>

          {/* Sorties caisse */}
          {(data?.sorties || []).length > 0 && (
            <div className="animate-slide-up" style={{ animationDelay: '150ms', animationFillMode: 'backwards' }}>
              <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))] mb-3">Sorties caisse</p>
              <div className="space-y-1">
                {data.sorties.map((s, i) => (
                  <div key={i} className="lm-card px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-mono text-[rgb(var(--lm-text-muted))]">{s.date}</span>
                      <span className="font-mono text-sm text-[rgb(var(--lm-red))]">-{formatCurrency(s.montant)}</span>
                    </div>
                    {s.nature && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-[rgb(var(--lm-surface-elevated))] rounded-sm text-[rgb(var(--lm-text-secondary))]">{s.nature}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rapprochements reussis */}
          {(data?.rapprochements || []).length > 0 && (
            <div className="animate-slide-up" style={{ animationDelay: '180ms', animationFillMode: 'backwards' }}>
              <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))] mb-3 flex items-center gap-2">
                <Check size={10} style={{ color: 'rgb(var(--lm-green))' }} /> Rapprochements valides
              </p>
              <div className="space-y-1">
                {data.rapprochements.map((r, i) => (
                  <div key={i} className="lm-card px-4 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div>
                        <span className="text-[10px] font-mono text-[rgb(var(--lm-text-muted))]">{r.date_caisse}</span>
                        <span className="font-mono text-sm text-[rgb(var(--lm-text))] ml-2">{formatCurrency(r.montant_caisse)}</span>
                      </div>
                      <ArrowRight size={12} className="text-[rgb(var(--lm-text-muted))]" />
                      <div>
                        <span className="text-[10px] font-mono text-[rgb(var(--lm-text-muted))]">{r.date_banque}</span>
                        <span className="font-mono text-sm ml-2" style={{ color: 'rgb(var(--lm-green))' }}>{formatCurrency(r.montant_banque)}</span>
                      </div>
                    </div>
                    {r.ecart !== 0 && (
                      <span className="text-[10px] font-mono text-[rgb(var(--lm-gold))]">Ecart: {formatCurrency(r.ecart)}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Non rapproches */}
          {(data?.non_rapproches_caisse || []).length > 0 && (
            <div className="animate-slide-up" style={{ animationDelay: '210ms', animationFillMode: 'backwards' }}>
              <p className="text-[9px] tracking-[0.3em] uppercase text-[rgb(var(--lm-text-muted))] mb-3 flex items-center gap-2">
                <X size={10} style={{ color: 'rgb(var(--lm-red))' }} /> Versements non rapproches (caisse)
              </p>
              <div className="space-y-1">
                {data.non_rapproches_caisse.map((d, i) => (
                  <div key={i} className="lm-card px-4 py-3 flex items-center justify-between" style={{ borderColor: 'rgb(var(--lm-red) / 0.2)' }}>
                    <div className="flex items-center gap-3">
                      <span className="text-[10px] font-mono text-[rgb(var(--lm-text-muted))]">{d.date}</span>
                      <span className="font-mono text-sm text-[rgb(var(--lm-red))]">{formatCurrency(d.montant)}</span>
                    </div>
                    <span className="text-[10px] text-[rgb(var(--lm-text-muted))]">Pas de mouvement bancaire correspondant</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

const KPI = ({ label, value, color }) => (
  <div className="lm-card p-3 text-center">
    <p className="text-[9px] tracking-wider uppercase text-[rgb(var(--lm-text-muted))]">{label}</p>
    <p className={`font-mono text-sm mt-1 ${
      color === 'gold' ? '' : color === 'green' ? 'text-[rgb(var(--lm-green))]' : color === 'red' ? 'text-[rgb(var(--lm-red))]' : 'text-[rgb(var(--lm-text))]'
    }`} style={color === 'gold' ? { color: 'rgb(var(--lm-gold))' } : {}}>
      {value}
    </p>
  </div>
);

export default Rapprochement;
